import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Div, Spinner, Text, Button, Textarea, Avatar, Group,
  Snackbar, Alert,
} from '@vkontakte/vkui';
import { Icon24Send, Icon28MessageOutline, Icon28CheckCircleOutline, Icon24Cancel } from '@vkontakte/icons';
import { api } from '../../api/client';
import { useWeddingStore } from '../../stores/wedding';
import { useAuthStore } from '../../stores/auth';
import type { Message, MessageAttachment, UploadedAttachment } from '../../types';
import { AttachmentView } from '../../ui/AttachmentView';

const PAGE_SIZE = 50;

function fmtTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function fmtDay(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Сегодня';
  if (d.toDateString() === yest.toDateString()) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const { currentWedding, members, loadMembers } = useWeddingStore();
  const user = useAuthStore((s) => s.user);
  const [items, setItems] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<UploadedAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ text: string; ok: boolean } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Message | null>(null);
  const [deleting, setDeleting] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<number | null>(null);
  const itemsRef = useRef<Message[]>(items);
  itemsRef.current = items;

  const load = useCallback(async () => {
    if (!currentWedding) return;
    try {
      const list = await api.get<Message[]>(`weddings/${currentWedding.id}/messages?limit=${PAGE_SIZE}`);
      setItems(list);
      setHasMore(list.length >= PAGE_SIZE);
    } catch {
      /* keep old on poll fail */
    } finally {
      setLoading(false);
    }
  }, [currentWedding]);

  const loadOlder = useCallback(async () => {
    if (!currentWedding || loadingOlder || itemsRef.current.length === 0) return;
    setLoadingOlder(true);
    try {
      const first = itemsRef.current[0];
      const older = await api.get<Message[]>(
        `weddings/${currentWedding.id}/messages?limit=${PAGE_SIZE}&before_id=${first.id}`
      );
      if (older.length === 0) {
        setHasMore(false);
      } else {
        setItems((prev) => [...older, ...prev]);
        setHasMore(older.length >= PAGE_SIZE);
      }
    } catch (e) {
      console.error('loadOlder', e);
    } finally {
      setLoadingOlder(false);
    }
  }, [currentWedding, loadingOlder]);

  useEffect(() => {
    void load();
    void loadMembers();
  }, [load, loadMembers]);

  // poll every 10s
  useEffect(() => {
    if (!currentWedding) return;
    pollRef.current = window.setInterval(() => {
      void load();
    }, 10000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [currentWedding, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [items.length]);

  async function uploadFiles(files: FileList | File[]) {
    if (!currentWedding) return;
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      for (const f of arr.slice(0, 10)) fd.append('files', f);
      const token = localStorage.getItem('colibri_token');
      const res = await fetch(`/api/weddings/${currentWedding.id}/messages/attachments`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) {
        let detail = `Upload error: ${res.status}`;
        try {
          const b = await res.json();
          if (b?.detail) detail = String(b.detail);
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const uploaded = (await res.json()) as UploadedAttachment[];
      setPending((prev) => [...prev, ...uploaded].slice(0, 10));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить файл');
    } finally {
      setUploading(false);
    }
  }

  async function send() {
    const body = text.trim();
    if ((!body && pending.length === 0) || !currentWedding || sending) return;
    setSending(true);
    setError(null);
    try {
      const msg = await api.post<Message>(`weddings/${currentWedding.id}/messages`, {
        body,
        attachment_ids: pending.map((a) => a.id),
      });
      setItems((prev) => [...prev, msg]);
      setText('');
      setPending([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отправить');
    } finally {
      setSending(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget || !currentWedding || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`weddings/${currentWedding.id}/messages/${deleteTarget.id}`);
      setItems((prev) => prev.filter((m) => m.id !== deleteTarget.id));
      setSnack({ text: 'Сообщение удалено', ok: true });
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      setSnack({ text: 'Не удалось удалить сообщение', ok: false });
    } finally {
      setDeleting(false);
    }
  }

  // group by day
  const groups: { day: string; msgs: Message[] }[] = [];
  for (const m of items) {
    const day = fmtDay(m.created_at);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.msgs.push(m);
    else groups.push({ day, msgs: [m] });
  }

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Чат
      </PanelHeader>

      <Div style={{ paddingBottom: 4 }}>
        <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
          Общий чат организаторов{currentWedding ? ` · ${currentWedding.name}` : ''}
        </Text>
      </Div>

      {(() => {
        const accepted = members.filter((m) => m.status === 'accepted');
        if (accepted.length === 0) return null;
        return (
          <Group header={<Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>В чате ({accepted.length})</Text>}>
            <Div style={{ display: 'flex', gap: 14, overflowX: 'auto', padding: '4px 12px 12px' }}>
              {accepted.map((m) => {
                const name = [m.user_first_name, m.user_last_name].filter(Boolean).join(' ') || 'Участник';
                const isMe = user && m.user_id === user.id;
                return (
                  <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56, gap: 4 }}>
                    <Avatar
                      size={40}
                      src={m.user_avatar_url || undefined}
                      initials={(m.user_first_name || 'У')[0]}
                      style={{ boxShadow: isMe ? '0 0 0 2px var(--vkui--color_background_accent)' : undefined }}
                    />
                    <Text style={{ fontSize: 11, textAlign: 'center', maxWidth: 64, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isMe ? 'Вы' : name}
                    </Text>
                  </div>
                );
              })}
            </Div>
          </Group>
        );
      })()}

      <Div style={{ minHeight: '50vh', paddingBottom: 12 }}>
        {hasMore && (
          <div style={{ textAlign: 'center', padding: '8px 0 12px' }}>
            <Button
              size="m"
              mode="secondary"
              loading={loadingOlder}
              onClick={() => void loadOlder()}
            >
              {loadingOlder ? 'Загрузка…' : 'Загрузить ранее'}
            </Button>
          </div>
        )}

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spinner size="m" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--vkui--color_text_secondary)' }}>
            <Icon28MessageOutline style={{ opacity: 0.35, marginBottom: 10 }} />
            <Text>Пока нет сообщений</Text>
            <Text style={{ fontSize: 13, marginTop: 4 }}>Напишите первое — сюда увидят все участники</Text>
          </div>
        )}

        {!loading && groups.map((g) => (
          <div key={g.day} style={{ marginBottom: 16 }}>
            <div style={{ textAlign: 'center', margin: '12px 0' }}>
              <span style={{
                fontSize: 12, color: 'var(--vkui--color_text_secondary)',
                background: 'var(--vkui--color_background_secondary)',
                padding: '2px 10px', borderRadius: 10,
              }}>
                {g.day}
              </span>
            </div>
            {g.msgs.map((m) => {
              const mine = user && m.sender_id === user.id;
              const name = [m.sender_first_name, m.sender_last_name].filter(Boolean).join(' ') || 'Участник';
              return (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    justifyContent: mine ? 'flex-end' : 'flex-start',
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      maxWidth: '78%',
                      background: mine
                        ? 'var(--vkui--color_background_accent)'
                        : 'var(--vkui--color_background_secondary)',
                      color: mine ? '#fff' : 'var(--vkui--color_text_primary)',
                      borderRadius: 14,
                      padding: '8px 12px',
                      borderBottomRightRadius: mine ? 4 : 14,
                      borderBottomLeftRadius: mine ? 14 : 4,
                      cursor: mine ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (mine) setDeleteTarget(m);
                    }}
                    title={mine ? 'Нажмите, чтобы удалить' : undefined}
                  >
                    {!mine && (
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2, opacity: 0.85 }}>
                        {name}
                      </div>
                    )}
                    {m.body && (
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                    )}
                    {(m.attachments?.length ?? 0) > 0 && (
                      <div style={{ marginTop: m.body ? 8 : 0, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {(m.attachments || []).map((a: MessageAttachment) => (
                          <AttachmentView key={a.id} url={a.file_url} variant="bubble" mine={!!mine} />
                        ))}
                      </div>
                    )}
                    <div style={{
                      fontSize: 11, marginTop: 3, textAlign: 'right',
                      opacity: 0.65,
                    }}>
                      {fmtTime(m.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={bottomRef} />
      </Div>

      {error && (
        <Div>
          <Text style={{ color: 'var(--vkui--color_text_negative)', fontSize: 13 }}>{error}</Text>
        </Div>
      )}

      {(pending.length > 0 || uploading) && (
        <div style={{
          position: 'sticky', bottom: 0,
          background: 'var(--vkui--color_background_content)',
          padding: '8px 12px 0',
          display: 'flex', flexWrap: 'wrap', gap: 8,
        }}>
          {uploading && <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>Загрузка файлов…</Text>}
          {pending.map((a) => (
            <div key={a.id} style={{ position: 'relative' }}>
              <AttachmentView key={a.id} url={a.file_url} variant="thumb" thumbSize={56} />
              <button
                type="button"
                onClick={() => setPending((prev) => prev.filter((x) => x.id !== a.id))}
                style={{
                  position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10,
                  border: 'none', background: 'var(--vkui--color_text_secondary)', color: '#fff',
                  fontSize: 12, cursor: 'pointer', lineHeight: '20px',
                }}
                aria-label="Убрать"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{
        position: 'sticky', bottom: 0,
        background: 'var(--vkui--color_background_content)',
        padding: '8px 12px 16px',
        borderTop: '1px solid var(--vkui--color_separator_primary)',
        display: 'flex', gap: 8, alignItems: 'flex-end',
      }}>
        <Textarea
          style={{ flex: 1 }}
          placeholder="Сообщение…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) void uploadFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <Button
          size="m"
          mode="secondary"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          style={{ minWidth: 44 }}
          aria-label="Прикрепить файл"
        >
          📎
        </Button>
        <Button
          size="m"
          mode="primary"
          disabled={(!text.trim() && pending.length === 0) || sending || uploading}
          onClick={() => void send()}
          style={{ minWidth: 44 }}
        >
          <Icon24Send />
        </Button>
      </div>

      {/* Delete confirm */}
      {deleteTarget && (
        <Alert
          actionsLayout="vertical"
          actions={[
            {
              title: deleting ? 'Удаление…' : 'Удалить сообщение',
              mode: 'destructive',
              // VKUI defers action until exit animation; onClose unmounts Alert first.
              autoCloseDisabled: true,
              action: (args) => {
                void handleDelete().finally(() => args?.close?.());
              },
            },
            {
              title: 'Отмена',
              mode: 'cancel',
            },
          ]}
          title="Удалить сообщение?"
          description={deleteTarget.body.length > 80 ? deleteTarget.body.slice(0, 77) + '…' : deleteTarget.body}
          onClose={() => setDeleteTarget(null)}
          onClosed={() => setDeleteTarget(null)}
        />
      )}

      {snack && (
        <Snackbar
          onClose={() => setSnack(null)}
          onClosed={() => setSnack(null)}
          before={
            snack.ok
              ? <Icon28CheckCircleOutline fill="var(--colibri-success)" />
              : <Icon24Cancel fill="var(--colibri-danger)" />
          }
        >
          {snack.text}
        </Snackbar>
      )}
    </Panel>
  );
}
